import moment from 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import UploadIcon from '../../../assets/svg/UploadIcon';
import { AI_PROMPT_MAX_LENGTH, DATE_TIME_FORMAT } from '../../../constants';
import {
  deleteAIPrompt,
  getAIPrompt,
  saveAIPrompt,
  setAIPromptEnabled,
} from '../../../services/aiPromptsApi';
import {
  BasicModal,
  Button,
  ErrorMessage,
  IconButton,
  Loader,
  ModalHeader,
} from '../../Shared';

const TOAST_OPTIONS = { position: 'top-right', autoClose: 3000 };

const NO_PROMPT_STATUS = {
  hasPrompt: false,
  isEnabled: false,
  updatedOn: null,
  updatedBy: null,
};

const getPromptError = prompt => {
  if (!prompt)
    return 'The prompt is empty. Paste the prompt or upload a .txt file.';
  if (prompt.length > AI_PROMPT_MAX_LENGTH) {
    return `The prompt is too long. The limit is ${AI_PROMPT_MAX_LENGTH.toLocaleString('en-US')} characters.`;
  }
  return '';
};

export const AIPromptModal = ({
  client,
  feature,
  shouldTurnOn,
  onClose,
  onChangeStatus,
}) => {
  const { tenantId, tenantName } = client;
  const { type, name } = feature;
  const { hasPrompt, updatedOn } = client[feature.key];

  const textareaRef = useRef(null);

  const [savedPrompt, setSavedPrompt] = useState('');
  const [prompt, setPrompt] = useState('');
  const [isPromptLoading, setIsPromptLoading] = useState(hasPrompt);
  const [isPromptError, setIsPromptError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoveStep, setIsRemoveStep] = useState(false);

  const lastUpdated = updatedOn
    ? moment(updatedOn).format(DATE_TIME_FORMAT.MMM_D_YYYY)
    : '';

  const fetchPrompt = useCallback(async () => {
    setIsPromptLoading(true);
    setIsPromptError(false);

    try {
      const text = await getAIPrompt(tenantId, type);
      setSavedPrompt(text);
      setPrompt(text);
    } catch {
      setIsPromptError(true);
    } finally {
      setIsPromptLoading(false);
    }
  }, [tenantId, type]);

  useEffect(() => {
    if (hasPrompt) fetchPrompt();
    // Only on open: saving or removing the prompt closes the window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangePrompt = text => {
    setPrompt(text);
    setErrorMessage('');
  };

  const onUploadFile = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      onChangePrompt(String(reader.result ?? ''));
      toast.success(
        `Loaded ${file.name}. Review it and click Save.`,
        TOAST_OPTIONS,
      );
    };
    reader.onerror = () => {
      toast.error(`Failed to read ${file.name}!`, TOAST_OPTIONS);
    };
    reader.readAsText(file);
  };

  const showError = message => {
    setErrorMessage(message);
    textareaRef.current?.focus();
  };

  const turnOn = async savedStatus => {
    try {
      const status = await setAIPromptEnabled(tenantId, type, true);
      onChangeStatus(tenantId, feature.key, status);
      toast.success(
        `Prompt saved. ${name} is on for ${tenantName}.`,
        TOAST_OPTIONS,
      );
    } catch {
      onChangeStatus(tenantId, feature.key, savedStatus);
      toast.error(
        `Prompt saved, but ${name} was not turned on. Try the switch again.`,
        TOAST_OPTIONS,
      );
    }
  };

  const onSave = async () => {
    const text = prompt.trim();
    const promptError = getPromptError(text);
    if (promptError) {
      showError(promptError);
      return;
    }

    setIsSubmitting(true);

    try {
      const status = await saveAIPrompt(tenantId, type, text);

      if (shouldTurnOn) {
        await turnOn(status);
      } else {
        onChangeStatus(tenantId, feature.key, status);
        toast.success(
          hasPrompt
            ? 'Prompt saved. New briefs use it from now on.'
            : `Prompt saved. Turn on ${name} for ${tenantName} with the switch.`,
          TOAST_OPTIONS,
        );
      }
      onClose();
    } catch (e) {
      setIsSubmitting(false);

      if (e.unknownFields?.length) {
        showError(
          `These fields don't exist in the Aidentified data: ${e.unknownFields.join(', ')}`,
        );
        return;
      }
      toast.error('Failed to save the prompt!', TOAST_OPTIONS);
    }
  };

  const onRemove = async () => {
    setIsSubmitting(true);

    try {
      await deleteAIPrompt(tenantId, type);
      onChangeStatus(tenantId, feature.key, NO_PROMPT_STATUS);
      toast.success(feature.getRemovedMessage(tenantName), TOAST_OPTIONS);
      onClose();
    } catch {
      setIsSubmitting(false);
      toast.error('Failed to remove the prompt!', TOAST_OPTIONS);
    }
  };

  // A long pasted prompt must not be lost on a stray click outside the window.
  const isDirty = prompt !== savedPrompt;
  const canCloseOutside = !isDirty && !isSubmitting;

  if (isRemoveStep) {
    return (
      <BasicModal
        open
        onClose={isSubmitting ? undefined : () => setIsRemoveStep(false)}
      >
        <div className="bg-white w-[520px] rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
          <ModalHeader
            title={`Remove the ${name} prompt?`}
            subtitle={`${tenantName} · last updated ${lastUpdated}`}
            disabled={isSubmitting}
            onClose={() => setIsRemoveStep(false)}
          />
          <p className="px-5 pb-2 text-base-1000 text-[16px]/[24px]">
            {feature.getRemoveEffect(tenantName)} To turn it on again, add the
            prompt.
          </p>
          <div className="flex items-center justify-end gap-3 py-4 px-5">
            <Button
              type="button"
              title="Cancel"
              variant="text"
              disabled={isSubmitting}
              onClick={() => setIsRemoveStep(false)}
            />
            <Button
              type="button"
              title="Remove prompt"
              variant="error"
              disabled={isSubmitting}
              onClick={onRemove}
            />
          </div>
        </div>
      </BasicModal>
    );
  }

  return (
    <BasicModal open onClose={canCloseOutside ? onClose : undefined}>
      <div className="bg-white w-[760px] max-w-full rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
        <ModalHeader
          title={`${name} prompt`}
          subtitle={`${tenantName} · ${hasPrompt ? `last updated ${lastUpdated}` : 'not added yet'}`}
          disabled={isSubmitting}
          onClose={onClose}
        />
        <div className="flex flex-col gap-4 px-5">
          {isPromptLoading && (
            <div className="h-[320px] flex justify-center items-center">
              <Loader />
            </div>
          )}
          {isPromptError && (
            <div className="h-[320px] flex flex-col justify-center items-center gap-3">
              <span className="text-base-800 text-[16px]/[24px]">
                Failed to load the prompt
              </span>
              <Button
                type="button"
                title="Reload"
                variant="blue"
                onClick={fetchPrompt}
              />
            </div>
          )}
          {!isPromptLoading && !isPromptError && (
            <>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="ai-prompt-text"
                  className="text-base-1000 font-medium text-[16px]/[24px]"
                >
                  Prompt
                </label>
                <textarea
                  id="ai-prompt-text"
                  ref={textareaRef}
                  value={prompt}
                  spellCheck={false}
                  placeholder="Paste the prompt or upload a .txt file"
                  disabled={isSubmitting}
                  onChange={e => onChangePrompt(e.target.value)}
                  className={twMerge(
                    'h-[320px] w-full resize-none rounded-xl border border-brand-300/30 p-3 text-base-1000 text-[14px]/[20px] outline-none focus:border-brand-300',
                    errorMessage && 'border-destructive-500',
                  )}
                />
                <div className="flex justify-between gap-3">
                  <ErrorMessage
                    errorMessage={errorMessage}
                    className="text-destructive-500 text-[14px]/[20px]"
                  />
                  <span className="shrink-0 text-base-800 text-[14px]/[20px]">
                    {prompt.length.toLocaleString('en-US')} /{' '}
                    {AI_PROMPT_MAX_LENGTH.toLocaleString('en-US')}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-base-1000 font-medium text-[16px]/[24px]">
                  Added automatically to every request
                </span>
                <ul className="pl-5 text-base-800 text-[14px]/[20px]">
                  {feature.autoContext.map(item => (
                    <li key={item} className="list-disc">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 py-4 px-5">
          <div className="flex items-center gap-2">
            {!isPromptLoading && !isPromptError && (
              <label
                title="Upload .txt"
                className="w-10 h-10 flex items-center justify-center rounded-full text-brand-900 cursor-pointer hover:bg-base-700"
              >
                <UploadIcon className="w-5 h-5" />
                <input
                  type="file"
                  accept=".txt,text/plain"
                  aria-label="Upload .txt"
                  hidden
                  disabled={isSubmitting}
                  onChange={onUploadFile}
                />
              </label>
            )}
            {hasPrompt && !isPromptLoading && !isPromptError && (
              <IconButton
                buttonProps={{
                  className: 'w-10 h-10 !rounded-full',
                  title: 'Remove prompt',
                  'aria-label': 'Remove prompt',
                  disabled: isSubmitting,
                  onClick: () => setIsRemoveStep(true),
                }}
                iconProps={{ name: 'trash', className: 'h-5' }}
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              title="Cancel"
              variant="text"
              disabled={isSubmitting}
              onClick={onClose}
            />
            <Button
              type="button"
              title={shouldTurnOn ? 'Save and turn on' : 'Save'}
              disabled={isSubmitting || isPromptLoading || isPromptError}
              onClick={onSave}
            />
          </div>
        </div>
      </div>
    </BasicModal>
  );
};
